import React, { useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { WorkshopBody, WorkshopDetailDTO } from "../../../../models/WorkshopDTO";
import S from "./strings";
import { Modal, Opciones, aInput, deInput, errorText, numOrNull } from "./comun";
import { WorkshopMultiDetailDTO } from "../../../../services/v2/workshops.service";
import { fechaHoraAmPm } from "../../../../utils/fechaHoraAmPm";
import { FechaHoraDMY } from "../../../../components/fechas/camposFecha";

interface Props {
	campusId: number;
	opciones: Opciones;
	taller: WorkshopDetailDTO | null;
	onClose: () => void;
	onSaved: (t: WorkshopDetailDTO, texto: string) => void;
}

/** Crear / editar un taller. El histórico no llega aquí. */
const WorkshopForm: React.FC<Props> = ({ campusId, opciones, taller, onClose, onSaved }) => {
	const http = HttpManager.getInstance();
	const [f, setF] = useState({
		name: taller?.name || "",
		startsAt: aInput(taller?.startsAt),
		endsAt: aInput(taller?.endsAt),
		courseId: taller?.courseId || 0,
		area: taller?.area || "",
		classroomId: taller?.classroomId || 0,
		mentorId: taller?.mentorId || 0,
		mentorFee: taller ? String(taller.mentorFee ?? "") : "",
		capacity: taller?.capacity != null ? String(taller.capacity) : "",
		price: taller ? String(taller.price ?? "") : "",
		notes: taller?.notes || "",
		invitationText: taller?.invitationText || "",
		status: (taller?.status === "historico" ? "programado" : taller?.status || "programado") as "programado" | "realizado" | "anulado"
	});
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const set = (k: keyof typeof f, v: any) => setF(x => ({ ...x, [k]: v }));
	//Con sesiones, inicio y fin los calcula el servidor desde la primera y la última sesión: aquí solo se leen.
	const conSesiones = ((taller as WorkshopMultiDetailDTO | null)?.sessions || []).length > 0 || ((taller as WorkshopMultiDetailDTO | null)?.sessionsCount || 0) > 0;
	const valido = f.name.trim().length > 1 && f.startsAt !== "" && f.price.trim() !== "" && !isNaN(Number(f.price));

	//Aulas y maestras que ya traía el taller aunque hoy no estén en la lista.
	const aulas = [...opciones.aulas.map(a => ({ id: a.id, name: a.buildingName ? `${a.name}, ${a.buildingName}` : a.name }))];
	if (taller?.classroomId && !aulas.some(a => a.id === taller.classroomId)) aulas.unshift({ id: taller.classroomId, name: taller.classroomName || "" });
	const maestras = opciones.maestras.map(m => ({ id: m.id, name: m.name }));
	if (taller?.mentorId && !maestras.some(m => m.id === taller.mentorId)) maestras.unshift({ id: taller.mentorId, name: taller.mentorName || "" });
	const cursos = [...opciones.cursos];
	if (taller?.courseId && !cursos.some(c => c.id === taller.courseId)) cursos.unshift({ id: taller.courseId, name: taller.courseName || "" });

	const guardar = () => {
		const body: WorkshopBody = {
			name: f.name.trim(), startsAt: deInput(f.startsAt) as string, endsAt: deInput(f.endsAt), price: Number(f.price),
			courseId: f.courseId || null, area: f.area.trim() || null, classroomId: f.classroomId || null, mentorId: f.mentorId || null,
			mentorFee: numOrNull(f.mentorFee), capacity: numOrNull(f.capacity), notes: f.notes.trim() || null, invitationText: f.invitationText.trim() || null
		};
		if (conSesiones) { delete (body as any).startsAt; delete body.endsAt; }
		setBusy(true); setError(null);
		const req = taller ? http.workshopsService.update(campusId, { ...body, id: taller.id, status: f.status }) : http.workshopsService.create(campusId, body);
		req.then(r => onSaved(r.data, taller ? S.guardado : S.creado))
			.catch(e => { setError(errorText(e)); setBusy(false); });
	};

	return (
		<Modal title={taller ? S.editarTaller : S.nuevo} onClose={onClose} ancho={680}>
			<div className="wau-fila" style={{ marginBottom: 10 }}>
				<label className="wau-campo" style={{ flex: "2 1 260px" }}><span className="wau-lab">{S.nombre}</span>
					<input className="wau-entrada" value={f.name} onChange={e => set("name", e.target.value)} autoFocus /></label>
				{taller ? (
					<label className="wau-campo" style={{ flex: "1 1 150px" }}><span className="wau-lab">{S.estado}</span>
						<select className="wau-entrada" value={f.status} onChange={e => set("status", e.target.value)}>
							<option value="programado">{S.estados.programado}</option>
							<option value="realizado">{S.estados.realizado}</option>
							<option value="anulado">{S.estados.anulado}</option>
						</select></label>
				) : null}
			</div>
			{taller && f.status === "anulado" && taller.status !== "anulado" ? <p className="wau-adv">{S.anularAviso}</p> : null}
			<div className="wau-fila" style={{ marginBottom: 10 }}>
				{conSesiones ? <>
					<label className="wau-campo" style={{ flex: "1 1 200px" }}><span className="wau-lab">{S.inicio}</span>
						<input className="wau-entrada wau-num wte-solo-lectura" readOnly value={fechaHoraAmPm(taller?.startsAt)} /></label>
					<label className="wau-campo" style={{ flex: "1 1 200px" }}><span className="wau-lab">{S.fin}</span>
						<input className="wau-entrada wau-num wte-solo-lectura" readOnly value={fechaHoraAmPm(taller?.endsAt)} /></label>
				</> : <>
					<div className="wau-campo" style={{ flex: "1 1 260px" }}><span className="wau-lab">{S.inicio}</span>
						<FechaHoraDMY value={f.startsAt} onChange={v => set("startsAt", v)} /></div>
					<div className="wau-campo" style={{ flex: "1 1 260px" }}><span className="wau-lab">{S.fin}</span>
						<FechaHoraDMY value={f.endsAt} onChange={v => set("endsAt", v)} /></div>
				</>}
			</div>
			<div className="wau-fila" style={{ marginBottom: 10 }}>
				<label className="wau-campo" style={{ flex: "1 1 220px" }}><span className="wau-lab">{S.curso}</span>
					<select className="wau-entrada" value={f.courseId} onChange={e => set("courseId", Number(e.target.value))}>
						<option value={0}>{S.sinCurso}</option>
						{cursos.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
					</select></label>
				<label className="wau-campo" style={{ flex: "1 1 200px" }}><span className="wau-lab">{S.area}</span>
					<input className="wau-entrada" value={f.area} onChange={e => set("area", e.target.value)} /></label>
			</div>
			<div className="wau-fila" style={{ marginBottom: 10 }}>
				<label className="wau-campo" style={{ flex: "1 1 200px" }}><span className="wau-lab">{S.aula}</span>
					<select className="wau-entrada" value={f.classroomId} onChange={e => set("classroomId", Number(e.target.value))}>
						<option value={0}>{S.ninguna}</option>
						{aulas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
					</select></label>
				<label className="wau-campo" style={{ flex: "1 1 200px" }}><span className="wau-lab">{S.maestra}</span>
					<select className="wau-entrada" value={f.mentorId} onChange={e => set("mentorId", Number(e.target.value))}>
						<option value={0}>{S.ninguna}</option>
						{maestras.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
					</select></label>
			</div>
			<div className="wau-fila" style={{ marginBottom: 10 }}>
				<label className="wau-campo" style={{ flex: "1 1 120px" }}><span className="wau-lab">{S.precio}</span>
					<input type="number" min={0} step="0.01" className="wau-entrada wau-num" value={f.price} onChange={e => set("price", e.target.value)} /></label>
				<label className="wau-campo" style={{ flex: "1 1 100px" }}><span className="wau-lab">{S.cupoMax}</span>
					<input type="number" min={1} className="wau-entrada wau-num" value={f.capacity} onChange={e => set("capacity", e.target.value)} /></label>
				<label className="wau-campo" style={{ flex: "1 1 140px" }}><span className="wau-lab">{S.pagoMaestra}</span>
					<input type="number" min={0} step="0.01" className="wau-entrada wau-num" value={f.mentorFee} onChange={e => set("mentorFee", e.target.value)} /></label>
			</div>
			<label className="wau-campo" style={{ marginBottom: 10 }}><span className="wau-lab">{S.textoInvitacion}</span>
				<textarea className="wau-entrada" rows={3} value={f.invitationText} onChange={e => set("invitationText", e.target.value)} /></label>
			<label className="wau-campo"><span className="wau-lab">{S.notas}</span>
				<textarea className="wau-entrada" rows={2} value={f.notes} onChange={e => set("notes", e.target.value)} /></label>
			{error ? <p className="wau-adv" style={{ marginTop: 8 }}>{error}</p> : null}
			<div className="wau-acciones">
				<button type="button" className="wau-btn s" onClick={onClose} disabled={busy}>{S.volver}</button>
				<button type="button" className="wau-btn p" onClick={guardar} disabled={busy || !valido}>{taller ? S.guardar : S.crear}</button>
			</div>
		</Modal>
	);
};

export default WorkshopForm;
